/**
 * Signing defaults for the older workflows, so they follow the same canon as
 * the newer builds.
 *
 * The steward always chooses: sign in the portal, or sign on paper in blue ink.
 * Both are honored. Foundational and covenant family instruments open on paper,
 * because those are signed at a sitting. Routine instruments open on the portal.
 * Original ink is required only where an outside party keeps the original, and
 * the reason is always said on the screen.
 */

import { BANK_INK_REASON, type SigningSpec } from './signingModel';

const SITTING_REASON = 'This one is signed at a sitting, so paper is the usual way.';

const DMV_INK_REASON = 'The motor vehicle office keeps the original before it changes the title.';

const RECORDER_INK_REASON = 'The county recorder keeps the original before it records the deed.';

/** Papers that are records only. Nobody signs them. */
const NO_SIGNATURE = new Set<string>([
  'form_member_information',
  'form_officer_information',
  'cover_member_records_filing',
  'contribution_summary',
  'filing_certificate',
  'resolution_format_guide',
  'resolution_distribution_list',
]);

const SPECS: Record<string, SigningSpec> = {
  // Member admission family
  resolution_member_appointment: { signing: 'stewards_choice', defaultInk: true, signingReason: SITTING_REASON },
  letter_member_invitation: { signing: 'stewards_choice', defaultInk: false },
  letter_member_acceptance: { signing: 'stewards_choice', defaultInk: true, signingReason: SITTING_REASON },
  membership_attestation: { signing: 'stewards_choice', defaultInk: true, signingReason: SITTING_REASON },

  // Officer appointment family
  resolution_officer_appointment: { signing: 'stewards_choice', defaultInk: true, signingReason: SITTING_REASON },
  letter_officer_appointment: { signing: 'stewards_choice', defaultInk: false },
  letter_officer_acceptance: { signing: 'stewards_choice', defaultInk: true, signingReason: SITTING_REASON },
  certificate_officer_appointment: { signing: 'stewards_choice', defaultInk: true, signingReason: SITTING_REASON },

  // Resolutions
  custom_resolution: { signing: 'stewards_choice', defaultInk: true, signingReason: SITTING_REASON },
  authorization_resolution: { signing: 'stewards_choice', defaultInk: true, signingReason: SITTING_REASON },
  acceptance_resolution: { signing: 'stewards_choice', defaultInk: true, signingReason: SITTING_REASON },

  // Giving assets into the care of the Association
  operating_agreement: { signing: 'stewards_choice', defaultInk: true, signingReason: SITTING_REASON },
  private_assignment: { signing: 'stewards_choice', defaultInk: true, signingReason: SITTING_REASON },
  ip_assignment: { signing: 'stewards_choice', defaultInk: true, signingReason: SITTING_REASON },
  property_stewardship: { signing: 'stewards_choice', defaultInk: true, signingReason: SITTING_REASON },
  vehicle_instrument: { signing: 'stewards_choice', defaultInk: true, signingReason: SITTING_REASON },

  // Papers an outside office keeps
  dmv_transfer: { signing: 'wet_required', signingReason: DMV_INK_REASON },
  deed_recording_cover: { signing: 'wet_required', signingReason: RECORDER_INK_REASON },
  bank_account_update: { signing: 'wet_required', signingReason: BANK_INK_REASON },

  // Sittings and the money record
  formal_meeting_minutes: { signing: 'stewards_choice', defaultInk: true, signingReason: SITTING_REASON },
  journal_record: { signing: 'stewards_choice', defaultInk: false },
};

/** True when this paper is a record only and carries no signature line. */
export function legacyRequiresSignature(key: string): boolean {
  return !NO_SIGNATURE.has(String(key || ''));
}

/** The signing default for an older workflow paper. */
export function legacySigningSpec(key: string): SigningSpec {
  return SPECS[String(key || '')] || { signing: 'stewards_choice', defaultInk: false };
}

/**
 * The signing fields to store on generated_documents.merge_field_data, so the
 * card on the screen and the saved choice both read the same way.
 */
export function legacySigningMeta(key: string, sealApplied: boolean) {
  const spec = legacySigningSpec(key);
  return {
    requires_signature: legacyRequiresSignature(key),
    signing: spec.signing,
    default_ink: spec.defaultInk ?? false,
    signing_reason: spec.signingReason ?? null,
    wet_ink_required: spec.signing === 'wet_required',
    sealed: sealApplied,
  };
}
